/* Endpoint de passagem de plantão — gera texto via IA */
import { fetchWithAuth } from './client';
import type { PassagemResponse } from '../types';

/** Caso registrado no plantão */
export interface CasoPlantao {
  hora: string;
  resumo: string;
}

/** Parâmetros pra geração de passagem (espelha PassagemPlantaoRequest do backend) */
export interface GerarPassagemParams {
  sessionId: string;
  plantaoId?: string | null;
  inicio?: number | null;
  casos?: CasoPlantao[];
  consultasIa?: number;
  creditosUsados?: number;
}

/**
 * Solicita ao backend a geração de passagem de plantão.
 * Claude sintetiza o histórico do plantão em formato de passagem médica estruturada.
 *
 * Endpoint: POST /api/ai/plantao/passagem
 * Autenticação: Bearer JWT (backend extrai userId do sub).
 *
 * Cleanup 09/05/2026: removido `getUserIdFromJwt()` helper + `userId` do body.
 * Backend `b3fbcb62` agora extrai userId do JWT Bearer (middleware tolerante 8.B.3).
 *
 * @param params - sessionId obrigatório; plantão data opcional (defaults zero)
 * @returns Texto da passagem gerado pela IA
 */
export async function gerarPassagemPlantao(params: GerarPassagemParams): Promise<string> {
  // Backend valida shape do plantao_context (plantao_id string, inicio number, casos array)
  // Usa fallbacks razoáveis quando frontend ainda não trackeia casos.
  const body = {
    sessionId: params.sessionId,
    plantao_id: params.plantaoId ?? `web-${params.sessionId}`,
    inicio: params.inicio ?? Date.now(),
    casos: params.casos ?? [],
    consultas_ia: params.consultasIa ?? 0,
    creditos_usados: params.creditosUsados ?? 0,
  };

  const response = await fetchWithAuth('/api/ai/plantao/passagem', {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(errorBody.error ?? 'Erro ao gerar passagem de plantão');
  }

  // Backend retorna { passagem } canonicamente. Aceita { content } por backcompat.
  const data = (await response.json()) as PassagemResponse & { passagem?: string };
  const text = data.passagem ?? data.content ?? '';
  if (!text) throw new Error('Resposta vazia do servidor');

  return text;
}
